import { normalizeIpId } from './myIpService';

export interface MyIpCreationTarget { projectId: string; roomId: string; canvasId?: string; }
/** 仅记录服务端已确认的创建步骤，未收到响应的请求不能推断为成功。 */
export interface MyIpCreationProgress {
  roomId?: string;
  projectId?: string;
  canvasId?: string;
  ipIds?: string[];
  tracked?: boolean;
}
export type MyIpCreationOrigin = { kind: 'new-project'; } | {
  kind: 'canvas'; projectId: string; roomId: string; canvasId: string;
};
export interface MyIpCreationHandoff {
  scopeKey: string;
  prefix: string;
  ipId: string;
  sourceSessionId: string;
  origin: MyIpCreationOrigin;
  target?: MyIpCreationTarget;
  progress?: MyIpCreationProgress;
}

const storagePrefix = 'roboneo:my-ip-creation:';

/** 业务 ID 按既有房间契约保留原始字符串，仅拒绝空值、控制字符和超长值。 */
function normalizeTargetId(value: unknown): string {
  // eslint-disable-next-line no-control-regex
  if (typeof value !== 'string' || !value.trim() || value.length > 64 || /[\u0000-\u001f\u007f]/.test(value)) throw new Error('Invalid IP creation target');
  return value;
}

/** 目标只包含固定业务 ID，调用方不能存入可执行的返回 URL。 */
function normalizeTarget(value: unknown): MyIpCreationTarget {
  if (!value || typeof value !== 'object') throw new Error('Missing IP creation target');
  const target = value as MyIpCreationTarget;
  return {
    projectId: normalizeTargetId(target.projectId),
    roomId: normalizeTargetId(target.roomId),
    ...(target.canvasId === undefined ? {} : { canvasId: normalizeTargetId(target.canvasId) }),
  };
}

/** 原画布必须同时绑定项目、房间与画布；普通入口只声明新建项目。 */
function normalizeOrigin(value: unknown): MyIpCreationOrigin {
  if (!value || typeof value !== 'object') throw new Error('Missing IP creation origin');
  const origin = value as MyIpCreationOrigin;
  if (origin.kind === 'new-project') return { kind: 'new-project' };
  if (origin.kind !== 'canvas') throw new Error('Invalid IP creation origin');
  const target = normalizeTarget(origin);
  return { kind: 'canvas', ...target, canvasId: normalizeTargetId(target.canvasId) };
}

/** 已确定的目标字段必须一致，重试不能导航到其他项目或画布。 */
function matchesTarget(expected: MyIpCreationTarget, actual: MyIpCreationTarget): boolean {
  return expected.projectId === actual.projectId && expected.roomId === actual.roomId
    && (expected.canvasId === undefined || expected.canvasId === actual.canvasId);
}

/** 分步记录必须按房间、项目、绑定的顺序完整，且只能包含本次创建的 IP。 */
function normalizeProgress(value: MyIpCreationProgress, ipId: string): MyIpCreationProgress {
  if (!value || typeof value !== 'object') throw new Error('Invalid IP creation progress');
  const progress: MyIpCreationProgress = {};
  for (const key of ['roomId', 'projectId', 'canvasId'] as const) {
    if (value[key] !== undefined) progress[key] = normalizeTargetId(value[key]);
  }
  if ((progress.projectId && (!progress.roomId || !progress.canvasId))
    || (progress.canvasId && !progress.projectId)) throw new Error('Incomplete IP creation progress');
  if (value.ipIds !== undefined) {
    if (!progress.projectId || !Array.isArray(value.ipIds)
      || value.ipIds.some((id) => normalizeIpId(id) !== ipId)) throw new Error('Invalid IP creation binding');
    progress.ipIds = [...new Set(value.ipIds.map(normalizeIpId))];
  }
  if (value.tracked !== undefined) {
    if (typeof value.tracked !== 'boolean' || !progress.projectId) throw new Error('Invalid IP creation tracking');
    progress.tracked = value.tracked;
  }
  return progress;
}

/** 只恢复完整创建来源与导航目标，旧记录中的额外字段不参与导航。 */
function normalizeRecord(value: unknown): MyIpCreationHandoff {
  if (!value || typeof value !== 'object') throw new Error('Missing IP creation handoff');
  const record = value as MyIpCreationHandoff;
  if (typeof record.scopeKey !== 'string' || !record.scopeKey
    || !['', '/--roboneo'].includes(record.prefix)) throw new Error('Invalid IP creation handoff');
  const origin = normalizeOrigin(record.origin);
  const target = record.target === undefined ? undefined : normalizeTarget(record.target);
  const ipId = normalizeIpId(record.ipId);
  const progress = record.progress === undefined ? undefined : normalizeProgress(record.progress, ipId);
  if (target && origin.kind === 'canvas' && !matchesTarget(origin, target)) throw new Error('Invalid IP creation destination');
  const expected = target || (origin.kind === 'canvas' ? origin : undefined);
  if (progress && expected && (['roomId', 'projectId', 'canvasId'] as const)
    .some((key) => progress[key] !== undefined && expected[key] !== undefined && progress[key] !== expected[key])) throw new Error('IP creation progress changed');
  return {
    scopeKey: record.scopeKey,
    prefix: record.prefix,
    ipId,
    sourceSessionId: normalizeTargetId(record.sourceSessionId),
    origin,
    ...(target ? { target } : {}),
    ...(progress ? { progress } : {}),
  };
}

/** 来源只作可选恢复缓存；浏览器存储不可用时仍使用当前内存上下文。 */
function writeRecord(record: MyIpCreationHandoff): MyIpCreationHandoff {
  try { window.sessionStorage.setItem(`${storagePrefix}${JSON.stringify([record.scopeKey, record.ipId])}`, JSON.stringify(record)); } catch { /* 可选缓存不阻塞创建。 */ }
  return record;
}

/** 按账号与 IP 缓存可选创建来源，不再生成 URL token。 */
export function rememberMyIpCreationHandoff(input: Pick<MyIpCreationHandoff, 'scopeKey' | 'prefix' | 'ipId' | 'sourceSessionId' | 'origin'>): MyIpCreationHandoff {
  return writeRecord(normalizeRecord(input));
}

/** 缓存缺失、损坏或存储不可用时忽略来源，IP 访问仍由服务端详情授权。 */
export function readMyIpCreationHandoff(ipId: unknown, scopeKey: string, expectedIpId?: string, sourceSessionId?: string): MyIpCreationHandoff | undefined {
  try {
    const id = normalizeIpId(ipId);
    const raw = window.sessionStorage.getItem(`${storagePrefix}${JSON.stringify([scopeKey, id])}`);
    const record = normalizeRecord(JSON.parse(raw || 'null'));
    if (record.scopeKey !== scopeKey || record.ipId !== id
      || (expectedIpId !== undefined && record.ipId !== normalizeIpId(expectedIpId))
      || (sourceSessionId !== undefined && record.sourceSessionId !== sourceSessionId)) return undefined;
    return record;
  } catch { return undefined; }
}

/** 在导航前缓存确定的目标，失败或刷新后复用已有项目，原画布来源不能被替换。 */
export function rememberMyIpCreationTarget(ipId: string, scopeKey: string, target: MyIpCreationTarget): MyIpCreationHandoff {
  const record = readMyIpCreationHandoff(ipId, scopeKey);
  const normalized = normalizeTarget(target);
  if (!record
    || (record.origin.kind === 'canvas' && !matchesTarget(record.origin, normalized))
    || (record.target && !matchesTarget(record.target, normalized))) throw new Error('IP creation destination changed');
  if (record.target) return record;
  return writeRecord(normalizeRecord({ ...record, target: normalized }));
}

/** 已确认的步骤只可向前推进；刷新后重试不得替换身份或丢掉已绑定的 IP。 */
export function rememberMyIpCreationProgress(ipId: string, scopeKey: string, progress: MyIpCreationProgress): MyIpCreationHandoff {
  const record = readMyIpCreationHandoff(ipId, scopeKey);
  if (!record) throw new Error('IP creation source is unavailable');
  const next = normalizeRecord({ ...record, progress });
  const previous = record.progress;
  if (previous && ((['roomId', 'projectId', 'canvasId'] as const)
    .some((key) => previous[key] !== undefined && previous[key] !== next.progress?.[key])
    || previous.ipIds?.some((id) => !next.progress?.ipIds?.includes(id))
    || (previous.tracked && !next.progress?.tracked))) throw new Error('IP creation progress cannot move backwards');
  return writeRecord(next);
}
